import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RefreshCw } from 'lucide-react';

const CompactAudioPlayer = ({ reciterImage, reciterName, surahName, audioUrl, allReciters, currentReciterId, onTimeUpdate, onEnded, onClose, onChangeReciter, trackDuration }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentReciterIndex, setCurrentReciterIndex] = useState(0);
  const [buttonTextIndex, setButtonTextIndex] = useState(0);
  const [audioError, setAudioError] = useState(false);
  const audioRef = useRef(null);
  const simulationTimerRef = useRef(null);
  const isSimulatingRef = useRef(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  // Aktif hafız DIŞINDA kalan diğer hafızlar
  const otherReciters = allReciters ? allReciters.filter(r => r.id !== currentReciterId) : [];
  
  // "Hafız" ve "Değiştir" döngüsü
  const buttonTexts = ['Hafız', 'Değiştir'];

  const getAudioUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const convertedUrl = url.replace('/uploads/', '/api/files/');
    return `${BACKEND_URL}${convertedUrl}`;
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const convertedUrl = url.replace('/uploads/', '/api/files/');
    return `${BACKEND_URL}${convertedUrl}`;
  };

  // Debug: Log all reciters
  useEffect(() => {
    if (allReciters) {
      console.log('========================================');
      console.log('[CompactPlayer] 📊 HAFIZ İSTATİSTİKLERİ:');
      console.log('[CompactPlayer] Toplam hafız sayısı:', allReciters.length);
      console.log('[CompactPlayer] Aktif hafız ID:', currentReciterId);
      console.log('[CompactPlayer] Diğer hafızlar sayısı:', otherReciters.length);
      console.log('[CompactPlayer] Diğer hafızların isimleri:', otherReciters.map(r => r.name).join(', '));
      console.log('========================================');
    }
  }, [allReciters, currentReciterId, otherReciters]);

  // Hafız resimlerini döngüsel olarak değiştir (sadece diğer hafızlar)
  useEffect(() => {
    if (!otherReciters || otherReciters.length === 0) {
      console.log('[CompactPlayer] ⚠️ Diğer hafız yok, resim animasyonu başlamıyor');
      return;
    }
    
    console.log('[CompactPlayer] 🔄 Resim animasyonu BAŞLADI -', otherReciters.length, 'hafız dönecek');
    
    const interval = setInterval(() => {
      setCurrentReciterIndex(prev => {
        const newIndex = (prev + 1) % otherReciters.length;
        console.log(`[CompactPlayer] 🖼️ Resim değişti: ${prev} → ${newIndex} (${otherReciters[newIndex]?.name})`);
        return newIndex;
      });
    }, 3000); // Her 3 saniyede bir değiş

    return () => {
      console.log('[CompactPlayer] 🛑 Resim animasyonu DURDURULDU');
      clearInterval(interval);
    };
  }, [otherReciters]);

  // "Hafız" / "Değiştir" yazısını döngüsel olarak değiştir
  useEffect(() => {
    const interval = setInterval(() => {
      setButtonTextIndex(prev => (prev + 1) % buttonTexts.length);
    }, 3000); // Her 3 saniyede bir değiş

    return () => clearInterval(interval);
  }, [buttonTexts.length]);

  useEffect(() => {
    const audio = audioRef.current;
    
    if (audio && audioUrl) {
      const fullUrl = getAudioUrl(audioUrl);
      console.log('[CompactPlayer] 🎵 ULTRA FAST LOADING:', fullUrl);
      
      // Reset state
      setIsPlaying(false);
      setCurrentTime(0);
      setAudioError(false);
      
      // Stop any existing simulation
      if (simulationTimerRef.current) {
        clearInterval(simulationTimerRef.current);
        simulationTimerRef.current = null;
      }
      isSimulatingRef.current = false;
      
      // Set fallback duration from prop if available
      if (trackDuration) {
        const parts = trackDuration.split(':');
        let totalSeconds = 0;
        if (parts.length === 3) {
          totalSeconds = parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
        } else if (parts.length === 2) {
          totalSeconds = parseInt(parts[0]) * 60 + parseInt(parts[1]);
        }
        if (totalSeconds > 0) {
          setDuration(totalSeconds);
        }
      }
      
      // CRITICAL: Set audio properties for fastest playback
      audio.preload = 'auto';
      audio.volume = 1.0;
      
      // Set src and load IMMEDIATELY
      audio.src = fullUrl;
      audio.load();
      
      let playSuccessful = false;
      const timeouts = [];
      
      // Start simulation mode if audio fails
      const startSimulation = (totalDuration) => {
        if (simulationTimerRef.current) {
          clearInterval(simulationTimerRef.current);
        }
        
        isSimulatingRef.current = true;
        setAudioError(true);
        let simulatedTime = 0;
        
        console.log('[CompactPlayer] 🎭 Starting simulation mode, duration:', totalDuration);
        
        simulationTimerRef.current = setInterval(() => {
          simulatedTime += 1;
          setCurrentTime(simulatedTime);
          
          if (onTimeUpdate) {
            onTimeUpdate(simulatedTime, totalDuration);
          }
          
          // Check if track ended
          if (simulatedTime >= totalDuration) {
            console.log('[CompactPlayer] 🎭 Simulation ended');
            clearInterval(simulationTimerRef.current);
            simulationTimerRef.current = null;
            isSimulatingRef.current = false;
            setIsPlaying(false);
            
            if (onEnded) {
              onEnded();
            }
          }
        }, 1000);
      };
      
      // SUPER AGGRESSIVE: Try to play every 50ms for first 500ms
      const aggressivePlay = () => {
        if (playSuccessful) return;
        
        audio.play()
          .then(() => {
            if (!playSuccessful) {
              console.log('[CompactPlayer] ✅ PLAYING NOW!');
              playSuccessful = true;
              setIsPlaying(true);
              setAudioError(false);
            }
          })
          .catch((err) => {
            // Check if it's a format error
            if (err.name === 'NotSupportedError' || 
                err.message?.includes('no supported streams') ||
                err.message?.includes('DEMUXER_ERROR')) {
              console.log('[CompactPlayer] ⚠️ Audio format not supported, starting simulation');
              if (!isSimulatingRef.current && duration > 0) {
                setIsPlaying(true);
                startSimulation(duration);
              }
            }
          });
      };
      
      // Audio error handler
      const handleError = () => {
        console.log('[CompactPlayer] ❌ Audio error, starting simulation');
        if (!isSimulatingRef.current && duration > 0) {
          setIsPlaying(true);
          startSimulation(duration);
        }
      };
      
      // Event listeners for optimal timing
      const handleLoadedMetadataEvent = () => {
        console.log('[CompactPlayer] 📊 Metadata loaded - PLAYING');
        if (audio.duration && !isNaN(audio.duration)) {
          setDuration(audio.duration);
        }
        aggressivePlay();
      };
      
      const handleLoadedData = () => {
        console.log('[CompactPlayer] 📦 Data loaded - PLAYING');
        aggressivePlay();
      };
      
      const handleCanPlay = () => {
        console.log('[CompactPlayer] ▶️ Can play - PLAYING');
        aggressivePlay();
      };
      
      const handleCanPlayThrough = () => {
        console.log('[CompactPlayer] ⚡ Can play through - PLAYING');
        aggressivePlay();
      };
      
      // Add ALL possible event listeners
      audio.addEventListener('loadedmetadata', handleLoadedMetadataEvent);
      audio.addEventListener('loadeddata', handleLoadedData);
      audio.addEventListener('canplay', handleCanPlay);
      audio.addEventListener('canplaythrough', handleCanPlayThrough);
      audio.addEventListener('error', handleError);
      
      // ULTRA AGGRESSIVE TIMEOUTS: Try every 50ms
      timeouts.push(setTimeout(aggressivePlay, 0));     // Immediate
      timeouts.push(setTimeout(aggressivePlay, 50));    // 50ms
      timeouts.push(setTimeout(aggressivePlay, 100));   // 100ms
      timeouts.push(setTimeout(aggressivePlay, 150));   // 150ms
      timeouts.push(setTimeout(aggressivePlay, 200));   // 200ms
      timeouts.push(setTimeout(aggressivePlay, 300));   // 300ms
      timeouts.push(setTimeout(aggressivePlay, 400));   // 400ms
      timeouts.push(setTimeout(aggressivePlay, 500));   // 500ms
      
      // Final fallback: FORCE PLAY after 600ms no matter what
      timeouts.push(setTimeout(() => {
        if (!playSuccessful && audio.paused) {
          console.log('[CompactPlayer] 🚨 FINAL FORCE PLAY (600ms)');
          audio.play()
            .then(() => {
              playSuccessful = true;
              setIsPlaying(true);
            })
            .catch((err) => {
              console.error('[CompactPlayer] ❌ All attempts failed:', err);
            });
        }
      }, 600));
      
      // Cleanup
      return () => {
        audio.removeEventListener('loadedmetadata', handleLoadedMetadataEvent);
        audio.removeEventListener('loadeddata', handleLoadedData);
        audio.removeEventListener('canplay', handleCanPlay);
        audio.removeEventListener('canplaythrough', handleCanPlayThrough);
        audio.removeEventListener('error', handleError);
        timeouts.forEach(t => clearTimeout(t));
        
        // Clean up simulation timer
        if (simulationTimerRef.current) {
          clearInterval(simulationTimerRef.current);
          simulationTimerRef.current = null;
        }
        isSimulatingRef.current = false;
        
        audio.pause();
        audio.src = '';
      };
    }
  }, [audioUrl]);

  const togglePlayPause = () => {
    if (isSimulatingRef.current) {
      // Handle simulation mode
      if (isPlaying) {
        // Pause simulation
        if (simulationTimerRef.current) {
          clearInterval(simulationTimerRef.current);
          simulationTimerRef.current = null;
        }
        setIsPlaying(false);
      } else {
        // Resume simulation
        if (duration > 0) {
          let simulatedTime = currentTime;
          simulationTimerRef.current = setInterval(() => {
            simulatedTime += 1;
            setCurrentTime(simulatedTime);
            
            if (onTimeUpdate) {
              onTimeUpdate(simulatedTime, duration);
            }
            
            if (simulatedTime >= duration) {
              clearInterval(simulationTimerRef.current);
              simulationTimerRef.current = null;
              isSimulatingRef.current = false;
              setIsPlaying(false);
              
              if (onEnded) {
                onEnded();
              }
            }
          }, 1000);
          setIsPlaying(true);
        }
      }
    } else if (audioRef.current) {
      // Handle normal audio mode
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(console.error);
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = (e) => {
    const audio = e.target;
    setCurrentTime(audio.currentTime);
    if (onTimeUpdate) {
      onTimeUpdate(audio.currentTime, audio.duration);
    }
  };

  const handleLoadedMetadata = (e) => {
    setDuration(e.target.duration);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    if (onEnded) {
      onEnded();
    }
  };

  const handleSeek = (e) => {
    const progressBar = e.currentTarget;
    const clickX = e.nativeEvent.offsetX;
    const width = progressBar.offsetWidth;
    const percentage = clickX / width;
    const newTime = percentage * duration;
    
    if (isSimulatingRef.current) {
      // Handle simulation mode seeking
      setCurrentTime(newTime);
      if (onTimeUpdate) {
        onTimeUpdate(newTime, duration);
      }
    } else if (audioRef.current) {
      // Handle normal audio mode seeking
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <>
      <audio
        ref={audioRef}
        preload="auto"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />
      
      <div className="fixed bottom-16 md:bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-[#8B7355] to-[#6B5744] shadow-2xl">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2.5">
          <div className="flex items-center gap-3">
            {/* Play/Pause Button - Hafız ile AYNI BOYUTTA */}
            <button
              onClick={togglePlayPause}
              className="flex-shrink-0 flex flex-col items-center group"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white hover:bg-white/90 rounded-full flex items-center justify-center shadow-lg transition-all">
                {isPlaying ? (
                  <Pause className="w-5 h-5 sm:w-6 sm:h-6 text-[#8B7355] fill-[#8B7355]" />
                ) : (
                  <Play className="w-5 h-5 sm:w-6 sm:h-6 text-[#8B7355] fill-[#8B7355] ml-0.5" />
                )}
              </div>
              {/* Durdur / Oynat Yazısı */}
              <span className="text-[9px] sm:text-[10px] text-white/80 group-hover:text-white font-semibold mt-0.5 transition-colors">
                {isPlaying ? 'Durdur' : 'Oynat'}
              </span>
            </button>

            {/* Mevcut Hafız Resmi - SOLA TAŞINDI */}
            <div className="flex-shrink-0 flex flex-col items-center">
              <div className="relative w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden border-2 border-green-400">
                <img 
                  src={getImageUrl(reciterImage)} 
                  alt={reciterName}
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Etkin Badge */}
              <span className="text-[9px] sm:text-[10px] text-green-400 font-semibold mt-0.5">
                Etkin
              </span>
            </div>

            {/* Track Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <div className="flex-1 min-w-0 mr-2">
                  <h3 className="text-xs sm:text-sm font-semibold text-white truncate">
                    {surahName}
                  </h3>
                  <p className="text-[10px] sm:text-xs text-white/80 truncate">
                    {reciterName}
                  </p>
                </div>
                <div className="text-[10px] sm:text-xs text-white/90 font-mono whitespace-nowrap">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>
              </div>

              {/* Progress Bar */}
              <div 
                className="relative h-1 sm:h-1.5 bg-white/30 rounded-full cursor-pointer"
                onClick={handleSeek}
              >
                <div 
                  className="absolute h-full bg-white rounded-full transition-all"
                  style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                />
              </div>
            </div>

            {/* Change Reciter Button - SAĞA TAŞINDI */}
            {onChangeReciter && otherReciters && otherReciters.length > 0 && (
              <button
                onClick={onChangeReciter}
                className="flex-shrink-0 group relative"
                title="Hafız Değiştir"
              >
                <div className="flex flex-col items-center">
                  {/* Animasyonlu Hafız Resimleri - SADECE DİĞER HAFIZLAR (Aktif hariç) */}
                  <div className="relative w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden border-2 border-white/50 group-hover:border-white transition-all shadow-lg">
                    {otherReciters.map((reciter, index) => (
                      <img
                        key={reciter.id || index}
                        src={getImageUrl(reciter.image)}
                        alt={reciter.name}
                        className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in-out ${
                          index === currentReciterIndex 
                            ? 'opacity-100 scale-100' 
                            : 'opacity-0 scale-95'
                        }`}
                      />
                    ))}
                    {/* Overlay Icon - Hover'da */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                      <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5 text-white opacity-0 group-hover:opacity-100 group-hover:rotate-180 transition-all duration-500" />
                    </div>
                    {/* Pulse Animasyonu */}
                    <div className="absolute inset-0 rounded-full border-2 border-white/30 animate-ping opacity-20"></div>
                  </div>
                  {/* "Hafız" / "Değiştir" Döngüsü */}
                  <span className="text-[9px] sm:text-[10px] text-white/80 group-hover:text-white font-semibold mt-0.5 transition-all duration-500">
                    {buttonTexts[buttonTextIndex]}
                  </span>
                </div>
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CompactAudioPlayer;
